/**
 * Utilitários compartilhados do hub de projetos.
 */
import React from 'react';
import type { OrgMember as HubOrgMember } from '../../hooks/useOrgMembers';
import type { ProjectHealth } from '../../types/enterpriseProject';
import {
  PROJECT_HEALTH_COLORS,
  PROJECT_HEALTH_LABELS,
} from '../../types/enterpriseProject';

export type ProjectHubTabId =
  | 'executive'
  | 'vsm'
  | 'actions'
  | 'roadmap'
  | 'management';

export const PROJECT_HUB_TAB_IDS: ProjectHubTabId[] = [
  'executive',
  'vsm',
  'actions',
  'roadmap',
  'management',
];

const LEGACY_TAB_ALIASES: Record<string, ProjectHubTabId> = {
  kpi: 'executive',
  performance: 'executive',
};

export function isProjectHubTabId(value: string | null): value is ProjectHubTabId {
  return !!value && PROJECT_HUB_TAB_IDS.includes(value as ProjectHubTabId);
}

export function resolveProjectHubTab(value: string | null): ProjectHubTabId {
  if (!value) return 'executive';
  if (isProjectHubTabId(value)) return value;
  return LEGACY_TAB_ALIASES[value] ?? 'executive';
}

export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function fmtCurrency(value: number): string {
  return `R$ ${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

export function resolveMemberName(
  userId: string | null | undefined,
  members: Array<Pick<HubOrgMember, 'id' | 'name'> & { user_id?: string; user_name?: string }>,
): string {
  if (!userId) return '—';
  const member = members.find(m => m.id === userId || m.user_id === userId);
  return member?.name ?? member?.user_name ?? userId.slice(0, 8);
}

export function isProjectOverdue(
  targetDate: string | null | undefined,
  status: string,
): boolean {
  return !!targetDate
    && new Date(targetDate) < new Date()
    && status !== 'completed'
    && status !== 'archived';
}

export function computeProjectScore(
  health: ProjectHealth,
  summary: ProjectPlansSummary,
  isOverdue: boolean,
): number {
  let score = 100;
  if (health === 'critical') score -= 35;
  else if (health === 'warning') score -= 18;
  score -= Math.min(summary.actions_overdue * 6, 24);
  score -= Math.min(summary.issues_critical * 8, 24);
  if (isOverdue) score -= 12;
  if (summary.executions_sla_risk > 0) score -= Math.min(summary.executions_sla_risk * 4, 12);
  return Math.max(0, Math.min(100, score));
}

export interface ProjectFinancialEstimate {
  roiPercent: number | null;
  economia: number;
  investimento: number;
}

export function deriveProjectFinancials(
  summary: ProjectPlansSummary,
  totalActionCost = 0,
  validatedGains = 0,
): ProjectFinancialEstimate {
  const economia = validatedGains * 18000 + summary.executions_completed * 12000;
  const investimento = totalActionCost > 0
    ? totalActionCost
    : summary.actions_total * 4500;
  const roiPercent = investimento > 0
    ? Math.round(((economia - investimento) / investimento) * 100)
    : null;
  return { roiPercent, economia, investimento };
}

export function derivePortfolioFinancials(summary?: ProjectPlansSummary): ProjectFinancialEstimate {
  if (!summary || summary.actions_total + summary.executions_total === 0) {
    return { roiPercent: null, economia: 0, investimento: 0 };
  }
  return deriveProjectFinancials(summary);
}

export function KpiChip({
  label,
  value,
  warn,
}: {
  label: string;
  value: number | string;
  warn?: boolean;
}) {
  return (
    <span>
      {label}:{' '}
      <strong className={warn ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}>
        {value}
      </strong>
    </span>
  );
}

export function ClickableMetric({
  label,
  value,
  onClick,
  tone = 'default',
  disabled,
}: {
  label: string;
  value: number | string;
  onClick?: () => void;
  tone?: 'default' | 'danger' | 'warning' | 'success' | 'muted';
  disabled?: boolean;
}) {
  const toneClass = {
    default: 'text-slate-800 dark:text-slate-100',
    danger: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
    success: 'text-emerald-600 dark:text-emerald-400',
    muted: 'text-slate-500 dark:text-slate-400',
  }[tone];

  const interactive = !!onClick && !disabled && value !== 0 && value !== '0' && value !== '—';

  return (
    <button
      type="button"
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      className={`rounded border border-slate-200 bg-white px-2 py-1.5 text-left dark:border-slate-800 dark:bg-slate-900 ${
        interactive ? 'cursor-pointer hover:border-blue-300 hover:bg-blue-50/40 dark:hover:border-blue-700 dark:hover:bg-blue-950/20' : 'cursor-default'
      }`}
    >
      <p className="text-[9px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-0.5 text-[15px] font-bold leading-tight ${toneClass}`}>{value}</p>
    </button>
  );
}

export function ProjectEmptyState({
  icon,
  title,
  description,
  actions,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-3 rounded-full border border-slate-200 bg-slate-50 p-3 text-slate-400 dark:border-slate-800 dark:bg-slate-900">
        {icon}
      </div>
      <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-200">{title}</p>
      <p className="mt-1 max-w-md text-[11px] text-slate-500 dark:text-slate-400">{description}</p>
      {actions && <div className="mt-4 flex flex-wrap items-center justify-center gap-2">{actions}</div>}
    </div>
  );
}

export function HealthScoreBadge({
  score,
  health,
}: {
  score: number;
  health: ProjectHealth;
}) {
  const ringColor = health === 'healthy'
    ? 'border-emerald-500 text-emerald-600'
    : health === 'warning'
      ? 'border-amber-500 text-amber-600'
      : 'border-red-500 text-red-600';

  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-14 w-14 items-center justify-center rounded-full border-[3px] ${ringColor}`}>
        <span className="text-[16px] font-bold">{score}</span>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500">Saúde Geral</p>
        <p className={`text-[12px] font-semibold ${PROJECT_HEALTH_COLORS[health]}`}>
          {PROJECT_HEALTH_LABELS[health]}
        </p>
      </div>
    </div>
  );
}

export function SectionPanel({
  title,
  icon,
  children,
  className = '',
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
        {icon}
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
          {title}
        </h3>
      </div>
      <div className="p-2">{children}</div>
    </section>
  );
}

export function secondaryButtonClassName() {
  return 'inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
}

export function primaryButtonClassName() {
  return 'inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700';
}
